package com.hyperether.goodjob.scenes.pricing

import androidx.compose.runtime.Composable
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.ui.geometry.Size
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.hyperether.goodjob.getPlatformChecker
import com.hyperether.goodjob.models.Plan
import com.hyperether.goodjob.models.Role
import com.hyperether.goodjob.models.User
import com.hyperether.goodjob.repository.Repository
import com.hyperether.goodjob.repository.remote.model.EphemeralKeyRequest
import com.hyperether.goodjob.repository.remote.model.EphemeralKeyResponse
import com.hyperether.goodjob.repository.remote.model.Resource
import com.hyperether.goodjob.repository.remote.model.SubscribeToPlanResponse
import com.hyperether.goodjob.resources.Res
import com.hyperether.goodjob.resources.monthly
import com.hyperether.goodjob.resources.no_data_received
import com.hyperether.goodjob.resources.nothing_to_cancel
import com.hyperether.goodjob.resources.plan_active_until
import com.hyperether.goodjob.resources.plan_downgrade_scheduled
import com.hyperether.goodjob.resources.unknown_error
import com.hyperether.goodjob.resources.you_are_subscribed
import com.hyperether.goodjob.util.DateTimeUtil
import com.hyperether.resources.AppLocale
import com.hyperether.resources.LocalizedStrings
import com.hyperether.resources.currentLanguage
import com.hyperether.resources.stringResource
import com.hyperether.resources.stringResourcePlain
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.launch
import org.lighthousegames.logging.logging

class PricingViewModel(val repository: Repository) : ViewModel() {

    val log = logging("PricingViewModel")
    val showLoader: MutableState<Boolean> = mutableStateOf(false)
    val selectedPlan: MutableState<String?> = mutableStateOf(null)
    val selectedPlanId: MutableState<String?> = mutableStateOf(null)
    val priceBoxSize: MutableState<Size> = mutableStateOf(Size.Zero)
    var selectedOption: MutableState<String> = mutableStateOf("")
    val pendingPlan: MutableState<Plan?> = mutableStateOf(null)

    val error = mutableStateOf<Resource<Error>?>(null)

    private val _plans = MutableStateFlow<List<Plan>>(emptyList())
    val plans: StateFlow<List<Plan>> = _plans
    val plansRemoteState = mutableStateListOf<Plan>()

    private val _plansRemote = MutableStateFlow<List<Plan>>(emptyList())
    val plansRemote: StateFlow<List<Plan>> = _plansRemote

    private val paymentIntentId = mutableStateOf<String?>(null)
    val amount = mutableStateOf<Double?>(null)
    val currency = mutableStateOf<String?>(null)
    val filteredPlans = mutableStateOf<List<Plan>>(emptyList())

    private val _isNavigationSourceDrawer = MutableStateFlow(false)
    val isNavigationSourceDrawer: StateFlow<Boolean> = _isNavigationSourceDrawer

    val showPopUpSuccess: MutableState<Boolean> = mutableStateOf(false)
    val showPopUpError: MutableState<Boolean> = mutableStateOf(false)
    val messageText: MutableState<String> = mutableStateOf("")
    val messageTextChangePlan: MutableState<String> = mutableStateOf("")
    val errorMessageTextChangePlan: MutableState<String> = mutableStateOf("")

    var enteredEmail: MutableState<String> = mutableStateOf("")

    init {
        log.d("PricingViewModel instance") { "_isNavigationSourceDrawer: ${isNavigationSourceDrawer.value}" }
        init()
    }

    fun init() {
        viewModelScope.launch {
            log.d("PricingViewModel init") { "_isNavigationSourceDrawer: ${isNavigationSourceDrawer.value} inst: ${this@PricingViewModel}" }
            selectedOption.value = LocalizedStrings.get(Res.string.monthly.key, AppLocale.DEFAULT)
            println("Get plans list ${_plans.value}")
            getAllPlans()
            if (plansRemoteState.isNotEmpty()) {
                println("Check plans list ${_plans.value}")
                val popularPlan =
                    plansRemoteState.find { it.isPopular == true && it.interval == "month" }
                selectedPlan.value = popularPlan?.getName(currentLanguage.value.code)
                selectedPlanId.value = popularPlan?._id
                amount.value = popularPlan?.price
                currency.value = popularPlan?.currency
            }
        }
    }

    suspend fun getAllPlans() {
        showLoader.value = true
        val result = repository.getAllPlans()
        if (result.data != null) {
            showLoader.value = false
            result.data.let { plansRemoteState.addAll(it) }
            val localPlaIds = plans.value.map { it._id }.toSet()

            val newPlans =
                result.data.filter { it._id != null && it._id !in localPlaIds }
                    .distinctBy { it._id }

            if (newPlans.isNotEmpty() && getPlatformChecker().isMobile()) {
                //newPlans.forEach { repository.insertPlan(it) }
                getAllPlansLocal()
            }
            _plansRemote.value = result.data
        } else {
            showLoader.value = false
        }
    }

    private fun getAllPlansLocal() {
        viewModelScope.launch {
            repository.getPlansFlow().collect { planList ->
                _plans.value = planList
            }
        }
    }

    fun setupPaymentIntent(
        onSuccess: (String) -> Unit,
        onError: (String) -> Unit,
        onUserLogin: () -> Unit
    ) {
        viewModelScope.launch {
            showLoader.value = true
            repository.getUser()?.customerId?.let {
                when (val result = repository.setupPaymentIntent(
                    paymentMethods = listOf("card", "paypal"),
                    customerId = it
                )) {
                    is Resource.Success -> {
                        showLoader.value = false
                        log.d("setupPaymentIntent") { "Success: ${result.data}" }
                        result.data?.clientSecret?.let { it1 -> onSuccess(it1) }
                    }

                    is Resource.Error -> {
                        showLoader.value = false
                        when (val result =
                            repository.getUser()?._id?.let { repository.getUserById(it) }) {
                            is Resource.Success -> {
                                result.data?.let {
                                    repository.saveUser(it)
                                }
                                onUserLogin()
                            }

                            is Resource.Error -> {
                                // Handle error state if needed
                            }

                            else -> {}
                        }


                        result.text?.let { errorMessageTextChangePlan.value = it }
                        error.value =
                            Resource.Error(
                                result.text ?: stringResourcePlain(Res.string.unknown_error)
                            )
                        onError(result.text ?: stringResourcePlain(Res.string.unknown_error))
                    }
                }
            }
        }
    }

    fun onPlanChosen(plan: Plan, errorText: String, onChoosePlanClick: (Double, String) -> Unit) {
        selectedPlan.value = plan.getName(currentLanguage.value.code)
        selectedPlanId.value = plan._id
        amount.value = plan.price
        currency.value = plan.currency
        amount.value?.let { amountValue ->
            currency.value?.let { currency ->
                onChoosePlanClick(amountValue, currency)
            } ?: run {
                error.value = Resource.Error(errorText)
            }
        } ?: run {
            error.value = Resource.Error(errorText)
        }
    }

    fun subscribeToPlan(
        onSuccess: (SubscribeToPlanResponse) -> Unit,
        onError: (String) -> Unit,
        onUserLogin: () -> Unit
    ) {
        log.d("subscribeToPlan") { "subscribeToPlan" }
        viewModelScope.launch {
            getUser()?.customerId?.let { customerId ->
                selectedPlanId.value?.let { selectedPlan ->
                    log.d("subscribeToPlan") { "planId: $selectedPlan" }
                    when (val result = repository.subscribeToPlan(selectedPlan, customerId)) {
                        is Resource.Success -> {
                            val data = result.data
                            log.d("subscribeToPlan") { "SUCCESS: $data" }
                            getUser()?.let {
                                it.pendingPlanId?.let { pendingPlanId -> getPlanById(pendingPlanId) }
                            }
                            data?.let { onSuccess(it) }
                        }

                        is Resource.Error -> {
                            log.d("subscribeToPlan") { "Error: ${result.text}" }
                            when (val result = repository.getUserById(customerId)) {
                                is Resource.Success -> {
                                    result.data?.let {
                                        repository.saveUser(it)
                                    }
                                    onUserLogin()
                                }

                                is Resource.Error -> {
                                    // Handle error state if needed
                                }
                            }

                            result.text?.let { onError(it) }
                        }
                    }
                } ?: run {
                    log.d("subscribeToPlan") { "Error: No planId found" }
                    onError("Error: No planId found")
                }
            } ?: run {
                log.d("subscribeToPlan") { "Error: No customerId found" }
                onError("Error: No customerId found")
            }
        }
    }

    fun getEphemeralKey(
        ephemeralKeyRequest: EphemeralKeyRequest,
        onSuccess: (EphemeralKeyResponse) -> Unit,
        onError: (String) -> Unit,
        onUserLogin: () -> Unit
    ) {
        viewModelScope.launch {
            when (val result = repository.getEphemeralKey(ephemeralKeyRequest)) {
                is Resource.Success -> {
                    val data = result.data
                    if (data != null) {
                        onSuccess(data)
                    } else {
                        error.value =
                            Resource.Error(stringResourcePlain(Res.string.no_data_received))
                        onError(stringResourcePlain(Res.string.no_data_received))
                    }
                }

                is Resource.Error -> {
                    when (val result =
                        repository.getUser()?._id?.let { repository.getUserById(it) }) {
                        is Resource.Success -> {
                            result.data?.let {
                                repository.saveUser(it)
                            }
                            onUserLogin()
                        }

                        is Resource.Error -> {
                            // Handle error state if needed
                        }

                        else -> {}
                    }

                    error.value =
                        Resource.Error(result.text ?: stringResourcePlain(Res.string.unknown_error))
                    onError(result.text ?: stringResourcePlain(Res.string.unknown_error))
                }
            }
        }
    }

    fun setNavigationFromDrawer(isNavigationFromDrawer: Boolean) {
        log.d("setNavigationFromDrawer") { "_isNavigationSourceDrawer: ${isNavigationFromDrawer}" }
        _isNavigationSourceDrawer.value = isNavigationFromDrawer
    }

    fun cancelSubscription() {
        viewModelScope.launch {
            val subscriptionId = repository.getUser()?.subscriptionId
            subscriptionId?.let {
                when (val result = repository.cancelSubscription(subscriptionId)) {
                    is Resource.Success -> {
                        val data = result.data
                        if (data != null) {
                            messageText.value = data.message
                            showPopUpSuccess.value = true
                        }
                    }

                    is Resource.Error -> {
                        result.text?.let { messageText.value = it }
                        showPopUpError.value = true
                    }
                }
            } ?: kotlin.run {
                messageText.value = stringResourcePlain(Res.string.nothing_to_cancel)
                showPopUpError.value = true
            }
        }
    }

    fun logout() {
        viewModelScope.launch {
            log.d { "PricingViewModel logout" }
            repository.logoutUser()
        }
    }

    fun subscribeUser() {
        viewModelScope.launch {
            when (val result = repository.subscribeUser(enteredEmail.value)) {
                is Resource.Success -> {
                    val data = result.data
                    if (data != null) {
                        messageText.value = stringResourcePlain(Res.string.you_are_subscribed)
                        showPopUpSuccess.value = true
                    }
                }

                is Resource.Error -> {
                    result.text?.let {
                        messageText.value = it
                    }
                    showPopUpError.value = true
                }
            }
        }
    }

    fun getUser(id: String) {
        viewModelScope.launch {
            when (val result = repository.getUserById(id)) {
                is Resource.Success -> {
                    result.data?.let {
                        repository.saveUser(it)
                    }
                }

                is Resource.Error -> {
                    // Handle error state if needed
                }
            }
        }
    }

    fun getPlanById(id: String) {
        viewModelScope.launch {
            pendingPlan.value = repository.getPlan(id)
        }
    }

    fun getUser(): User? {
        return repository.getUser()
    }

    fun isEmployee(): Boolean {
        return repository.getUser()?.role.equals(Role.employee.name, ignoreCase = true)
    }

    fun loadAllJobs() {
        viewModelScope.launch {
            if (isEmployee()) {
                repository.getAllJobsForUser()
            } else {
                repository.getAllJobsForWorkspace()
            }
        }
    }

    suspend fun getPlanFromWorkspace(): Plan? {
        repository.getWorkspaceById(repository.getWorkspaceId())?.planId?.let {
            return repository.getPlan(it)
        }
        return null
    }

    fun updateFilteredPlans() {
        filteredPlans.value = emptyList()
        val newFilteredPlans = mutableListOf<Plan>()

        val plans = if (getPlatformChecker().isMobile()) {
            plans.value
        } else {
            plansRemote.value.sortedBy { it.price }
        }
        // Always add the "freebie" plan if it exists
        val freebiePlan = plans.find {
            it.price == 0.0
        }
        if (freebiePlan != null) {
            newFilteredPlans.add(freebiePlan)
        }

        // Filter plans based on the selected interval ("month" or "year")
        val selectedInterval = when {
            selectedOption.value.startsWith("year", ignoreCase = true) -> "year"
            selectedOption.value.startsWith("month", ignoreCase = true) -> "month"
            else -> null
        }

        if (selectedInterval != null) {
            val matchingPlans = plans.filter {
                it.interval.equals(selectedInterval, ignoreCase = true) && it.price != 0.0
            }

            // Add up to 3 plans matching the interval
            newFilteredPlans.addAll(matchingPlans)
        }

        // Update the filtered plans observable state
        filteredPlans.value = newFilteredPlans
        log.d("Pricing") { "Filtered plans: ${filteredPlans.value}" }
    }

    @Composable
    fun getPlanChangeMessage(): String {
        val planActiveText = stringResource(Res.string.plan_active_until).replace("*", "") +
                getUser()?.subscriptionEndsAt?.let {
                    DateTimeUtil.formatDateForDisplay(DateTimeUtil.extractDate(it))
                }
        val downgradeScheduledText = stringResource(Res.string.plan_downgrade_scheduled) +
                pendingPlan.value?.getName(currentLanguage.value.code)

        if (getUser()?.pendingPlanId != null)
            return "$planActiveText $downgradeScheduledText"
        else
            return messageTextChangePlan.value
    }
}